import React, { useState } from 'react';
import { X, Save, RotateCcw } from 'lucide-react';
import { ScoringWeights } from '../types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  weights: ScoringWeights;
  onWeightsChange: (weights: ScoringWeights) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  weights,
  onWeightsChange
}) => {
  const [tempWeights, setTempWeights] = useState<ScoringWeights>(weights);

  const handleSave = () => {
    onWeightsChange(tempWeights);
    onClose();
  };

  const handleReset = () => {
    setTempWeights({ attendance: 0.3, experience: 0.2, contributionGap: 0.5, minCurrentScore: 80 });
  };

  const updateWeight = (key: keyof ScoringWeights, value: number) => {
    setTempWeights(prev => ({ ...prev, [key]: value }));
  };

  const totalWeight = tempWeights.attendance + tempWeights.experience + tempWeights.contributionGap;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-medium max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-secondary-900">
              Scoring Weights
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-secondary-400 hover:text-secondary-600 transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Description */}
          <div className="mb-6 p-4 bg-secondary-50 rounded-lg">
            <p className="text-sm text-secondary-600">
              Adjust the relative importance of each factor in the final Hot Seat score calculation. 
              The total weight should equal 1.0 (100%).
            </p>
          </div>

          {/* Weight Controls */}
          <div className="space-y-6">
            {/* Attendance Weight */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-secondary-700">
                  Green Score (Weightage)
                </label>
                <span className="text-sm text-secondary-500">
                  {(tempWeights.attendance * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={tempWeights.attendance}
                onChange={(e) => updateWeight('attendance', parseFloat(e.target.value))}
                className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <p className="text-xs text-secondary-500 mt-1">
                Based on green score
              </p>
            </div>

            {/* Experience Weight */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-secondary-700">
                  Experience (Weightage)
                </label>
                <span className="text-sm text-secondary-500">
                  {(tempWeights.experience * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={tempWeights.experience}
                onChange={(e) => updateWeight('experience', parseFloat(e.target.value))}
                className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <p className="text-xs text-secondary-500 mt-1">
                Based on years of BNI membership
              </p>
            </div>

            {/* Revenue Received Weight */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-secondary-700">
                  Revenue Received (Weightage)
                </label>
                <span className="text-sm text-secondary-500">
                  {(tempWeights.contributionGap * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={tempWeights.contributionGap}
                onChange={(e) => updateWeight('contributionGap', parseFloat(e.target.value))}
                className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <p className="text-xs text-secondary-500 mt-1">
                Lower revenue received = higher score
              </p>
            </div>

            {/* Minimum Green Score */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-secondary-700">
                  Minimum Green Score
                </label>
                <span className="text-sm text-secondary-500">
                  {tempWeights.minCurrentScore.toFixed(0)}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={tempWeights.minCurrentScore}
                onChange={(e) => updateWeight('minCurrentScore', parseFloat(e.target.value))}
                className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <p className="text-xs text-secondary-500 mt-1">
                Minimum attendance score required for qualification
              </p>
            </div>
          </div>

          {/* Total Weight Warning */}
          {Math.abs(totalWeight - 1.0) > 0.01 && (
            <div className="mt-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
              <p className="text-sm text-accent-700">
                <strong>Warning:</strong> Total weight is {(totalWeight * 100).toFixed(0)}%. 
                It should equal 100% for accurate scoring.
              </p>
            </div>
          )}

          {/* Formula Display */}
          <div className="mt-6 p-4 bg-primary-50 rounded-lg">
            <h4 className="text-sm font-medium text-primary-900 mb-2">Current Formula:</h4>
            <p className="text-xs text-primary-700 font-mono">
              Final Score = {(tempWeights.attendance * 100).toFixed(0)}% × Green Score + {' '}
              {(tempWeights.experience * 100).toFixed(0)}% × Years in BNI + {' '}
              {(tempWeights.contributionGap * 100).toFixed(0)}% × Revenue Received
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-secondary-200">
            <button
              onClick={handleReset}
              className="btn-secondary flex items-center space-x-2"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
